/**
 * Session Recording Context
 * 
 * Centralized management of session recording across the PWA.
 * Enables recording to persist across page navigations (e.g., Auth → Callback → Onboarding).
 * 
 * Features:
 * - Prevents double-start of recordings
 * - Auto-cleanup after 5 minutes of inactivity
 * - Flow type tracking for telemetry
 * - Seamless integration with sessionRecorder singleton
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { sessionRecorder } from '../lib/sessionRecorder';

export type FlowType = 'auth' | 'registration' | 'topup' | 'payment';

interface SessionRecordingContextValue {
  /** Start recording for a specific flow type */
  startFlow: (flowType: FlowType) => Promise<void>;
  /** Stop recording and upload to storage, linked to a critical event */
  stopAndUpload: (criticalEventId: string, guestSessionId?: string) => Promise<string | null>;
  /** Discard current recording without uploading (use on success) */
  discardRecording: () => void;
  /** Currently active flow type, null if not recording */
  currentFlow: FlowType | null;
  /** Whether recording is currently active */
  isRecording: boolean;
}

const SessionRecordingContext = createContext<SessionRecordingContextValue | null>(null);

// Auto-cleanup timeout (5 minutes)
const AUTO_CLEANUP_MS = 5 * 60 * 1000;

export function SessionRecordingProvider({ children }: { children: React.ReactNode }) {
  const [currentFlow, setCurrentFlow] = useState<FlowType | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const autoCleanupRef = useRef<NodeJS.Timeout | null>(null);

  // Clear auto-cleanup timer
  const clearAutoCleanup = useCallback(() => {
    if (autoCleanupRef.current) {
      clearTimeout(autoCleanupRef.current);
      autoCleanupRef.current = null;
    }
  }, []);

  // Set auto-cleanup timer
  const setAutoCleanup = useCallback(() => {
    clearAutoCleanup();
    autoCleanupRef.current = setTimeout(() => {
      console.log('[SessionRecording] Auto-cleanup triggered after 5 min inactivity');
      sessionRecorder.discardRecording();
      setCurrentFlow(null);
      setIsRecording(false);
    }, AUTO_CLEANUP_MS);
  }, [clearAutoCleanup]);

  // Start recording for a flow
  const startFlow = useCallback(async (flowType: FlowType) => {
    // Check if already recording same flow
    if (isRecording && currentFlow === flowType) {
      console.log(`[SessionRecording] Already recording ${flowType} flow`);
      return;
    }

    // If recording different flow, discard and start new
    if (isRecording && currentFlow !== flowType) {
      console.log(`[SessionRecording] Switching from ${currentFlow} to ${flowType}`);
      sessionRecorder.discardRecording();
    }

    console.log(`[SessionRecording] Starting ${flowType} flow recording`);
    await sessionRecorder.startRecording();
    setCurrentFlow(flowType);
    setIsRecording(true);
    setAutoCleanup();
  }, [isRecording, currentFlow, setAutoCleanup]);

  // Stop and upload recording
  const stopAndUpload = useCallback(async (criticalEventId: string, guestSessionId?: string): Promise<string | null> => {
    if (!isRecording) {
      console.log('[SessionRecording] No active recording to upload');
      return null;
    }

    clearAutoCleanup();
    console.log(`[SessionRecording] Uploading ${currentFlow} flow recording for event ${criticalEventId}`, 
      guestSessionId ? `(guest: ${guestSessionId})` : '(authenticated)');
    
    const path = await sessionRecorder.stopAndUpload(criticalEventId, guestSessionId);
    setCurrentFlow(null);
    setIsRecording(false);
    
    return path;
  }, [isRecording, currentFlow, clearAutoCleanup]);

  // Discard recording without uploading
  const discardRecording = useCallback(() => {
    if (!isRecording) return;
    
    clearAutoCleanup();
    console.log(`[SessionRecording] Discarding ${currentFlow} flow recording`);
    sessionRecorder.discardRecording();
    setCurrentFlow(null);
    setIsRecording(false);
  }, [isRecording, currentFlow, clearAutoCleanup]);

  // Sync state with singleton on mount (in case recording was started elsewhere)
  useEffect(() => {
    if (sessionRecorder.isRecording() && !isRecording) {
      setIsRecording(true);
      // We don't know the flow type, but at least track recording state
    }
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAutoCleanup();
    };
  }, [clearAutoCleanup]);

  const value: SessionRecordingContextValue = {
    startFlow,
    stopAndUpload,
    discardRecording,
    currentFlow,
    isRecording,
  };

  return (
    <SessionRecordingContext.Provider value={value}>
      {children}
    </SessionRecordingContext.Provider>
  );
}

/**
 * Hook to access session recording functions
 * Must be used within SessionRecordingProvider
 */
export function useSessionRecording(): SessionRecordingContextValue {
  const context = useContext(SessionRecordingContext);
  if (!context) {
    throw new Error('useSessionRecording must be used within SessionRecordingProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if outside provider
 * Useful for components that might be used in both PWA and admin contexts
 */
export function useSessionRecordingOptional(): SessionRecordingContextValue | null {
  return useContext(SessionRecordingContext);
}
